import { usePublicContent } from '../hooks/usePublicContent';

export const DonacionesPage = () => {
  const { getText, getJson } = usePublicContent('DONACIONES');

  const headerLabel = getText('header', 'label', 'Generosidad');
  const headerTitle = getText('header', 'titulo', 'Donaciones y Ofrendas');
  const headerSubtitle = getText('header', 'subtitulo', 'Tu generosidad permite que la obra de Dios continúe creciendo y bendiciendo vidas');
  const verse = getText('header', 'versiculo', 'Dad, y se os dará; medida buena, apretada, remecida y rebosando darán en vuestro regazo');
  const verseReference = getText('header', 'referencia', '- Lucas 6:38');

  const methodsLabel = getText('metodos', 'label', 'Contribuye');
  const methodsTitle = getText('metodos', 'titulo', 'Formas de Donar');
  const methodsDescription = getText('metodos', 'descripcion', 'Elige la modalidad que más se adapte a ti');
  const formasDonacion = getJson<{ titulo: string; descripcion: string; icono: string; detalles: string[] }[]>('metodos', 'items', [
    { titulo: 'Transferencia Bancaria', descripcion: 'Realiza tu donación directamente a nuestra cuenta bancaria', icono: '🏦', detalles: ['Banco: [Nombre del Banco]', 'Cuenta: [Número de cuenta]', 'A nombre de: Iglesia Cristiana Cuadrangular'] },
    { titulo: 'PSE - Pago en Línea', descripcion: 'Dona de forma segura a través de PSE desde tu banco', icono: '💳', detalles: ['Pago seguro y verificado', 'Disponible las 24 horas', 'Recibo automático por email'] },
    { titulo: 'Nequi / Daviplata', descripcion: 'Dona desde tu billetera digital favorita', icono: '📱', detalles: ['Nequi: [Número]', 'Daviplata: [Número]', 'Confirmación instantánea'] },
    { titulo: 'Presencial', descripcion: 'Entrega tu ofrenda durante los servicios', icono: '⛪', detalles: ['Durante los cultos', 'Oficina administrativa', 'Efectivo o cheque'] },
  ]);
  const donateButton = getText('metodos', 'boton', 'Donar Ahora');

  const quickLabel = getText('rapida', 'label', 'Rápido');
  const quickTitle = getText('rapida', 'titulo', 'Donación Rápida');
  const quickDescription = getText('rapida', 'descripcion', 'Selecciona un monto predeterminado para donar de forma rápida');
  const quickAmounts = getJson<string[]>('rapida', 'montos', ['$10,000', '$25,000', '$50,000', '$100,000']);
  const quickPlaceholder = getText('rapida', 'placeholder', 'Otro monto');
  const quickButton = getText('rapida', 'boton', 'Donar');

  const infoLabel = getText('info', 'label', 'Confianza');
  const infoTitle = getText('info', 'titulo', 'Información Importante');
  const infoItems = getJson<{ icono: string; titulo: string; descripcion: string }[]>('info', 'items', [
    { icono: '🔒', titulo: 'Seguridad', descripcion: 'Todas las transacciones son seguras y están protegidas con encriptación SSL' },
    { icono: '📄', titulo: 'Recibo', descripcion: 'Recibirás un comprobante de donación por email para efectos tributarios' },
    { icono: '💰', titulo: 'Transparencia', descripcion: 'Publicamos informes financieros trimestrales para transparencia total' },
  ]);

  const formasDonacionOriginal = [
    {
      titulo: "Transferencia Bancaria",
      descripcion: "Realiza tu donación directamente a nuestra cuenta bancaria",
      icono: "🏦",
      detalles: [
        "Banco: [Nombre del Banco]",
        "Cuenta: [Número de cuenta]",
        "A nombre de: Iglesia Cristiana Cuadrangular"
      ]
    },
    {
      titulo: "PSE - Pago en Línea",
      descripcion: "Dona de forma segura a través de PSE desde tu banco",
      icono: "💳",
      detalles: [
        "Pago seguro y verificado",
        "Disponible las 24 horas",
        "Recibo automático por email"
      ]
    },
    {
      titulo: "Nequi / Daviplata",
      descripcion: "Dona desde tu billetera digital favorita",
      icono: "📱",
      detalles: [
        "Nequi: [Número]",
        "Daviplata: [Número]",
        "Confirmación instantánea"
      ]
    },
    {
      titulo: "Presencial",
      descripcion: "Entrega tu ofrenda durante los servicios",
      icono: "⛪",
      detalles: [
        "Durante los cultos",
        "Oficina administrativa",
        "Efectivo o cheque"
      ]
    }
  ];

  const destinosOfrendas = [
    {
      titulo: "Sostenimiento de la Iglesia",
      descripcion: "Mantener las instalaciones y servicios básicos",
      porcentaje: "40%",
      color: "from-red-600 to-red-500"
    },
    {
      titulo: "Obra Misionera",
      descripcion: "Apoyo a misioneros y evangelización",
      porcentaje: "25%",
      color: "from-blue-600 to-blue-500"
    },
    {
      titulo: "Ayuda Social",
      descripcion: "Programas de ayuda a familias necesitadas",
      porcentaje: "20%",
      color: "from-yellow-600 to-yellow-500"
    },
    {
      titulo: "Ministerios",
      descripcion: "Apoyo a los diferentes ministerios de la iglesia",
      porcentaje: "15%",
      color: "from-purple-600 to-purple-500"
    }
  ];

  return (
    <div className="min-h-screen transition-colors duration-300 bg-stone-100">
      
      {/* Header */}
      <section className="relative overflow-hidden pt-32 pb-20 px-6">
        <div className="absolute inset-0 bg-stone-200/30"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium animate-fade-in">{headerLabel}</p>
          <h1 className="text-5xl md:text-6xl font-serif font-bold mb-2 animate-fade-in text-stone-800">
            {headerTitle}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full animate-slide-up delay-100"></div>
          <p className="text-xl text-stone-600 animate-slide-up delay-200 max-w-3xl mx-auto">
            {headerSubtitle}
          </p>
          
          <div className="mt-8 p-6 bg-white/90 rounded-2xl backdrop-blur-sm max-w-2xl mx-auto border border-stone-200/60 shadow-sm">
            <blockquote className="text-lg italic font-serif text-stone-700 mb-4">
              "{verse}"
            </blockquote>
            <cite className="text-purple-700 font-semibold">{verseReference}</cite>
          </div>
        </div>
      </section>

      {/* Formas de Donación */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium text-center">{methodsLabel}</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-stone-800 mb-2 text-center">
            {methodsTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
          <p className="text-center text-stone-500 mb-16">
            {methodsDescription}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {formasDonacion.map((forma, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200/60 hover:shadow-lg transition-all duration-300 animate-slide-up group"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-stone-200 transition-colors">
                  <span className="text-stone-600 text-2xl">{forma.icono}</span>
                </div>
                
                <h3 className="text-xl font-serif font-bold text-stone-800 text-center mb-3">
                  {forma.titulo}
                </h3>
                <div className="w-8 h-px bg-stone-300 mx-auto mb-4"></div>
                
                <p className="text-stone-600 text-sm text-center mb-6 leading-relaxed">
                  {forma.descripcion}
                </p>
                
                <div className="space-y-2 mb-6">
                  {forma.detalles.map((detalle, idx) => (
                    <p key={idx} className="text-xs text-stone-500 text-center">
                      {detalle}
                    </p>
                  ))}
                </div>
                
                <button className="w-full bg-purple-800 text-white py-3 px-6 rounded-xl font-semibold hover:bg-purple-700 transition-all duration-300 shadow-sm">
                  {donateButton}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donación Rápida */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-stone-200/30 rounded-2xl p-12 text-center border border-stone-200/60">
            <p className="text-stone-500 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{quickLabel}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-2 text-stone-800">
              {quickTitle}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-stone-600 mb-8">
              {quickDescription}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {quickAmounts.map((monto) => (
                <button 
                  key={monto}
                  className="bg-white border border-stone-300 hover:border-purple-700 text-stone-800 py-4 px-6 rounded-xl font-semibold hover:bg-purple-800 hover:text-white transition-all duration-300"
                >
                  {monto}
                </button>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <input
                type="number"
                placeholder={quickPlaceholder}
                className="px-6 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-white"
              />
              <button className="bg-purple-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg">
                {quickButton}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Información Adicional */}
      <section className="py-24 px-6 bg-stone-800 text-stone-100">
        <div className="max-w-6xl mx-auto">
          <p className="text-stone-400 text-sm uppercase tracking-[0.3em] mb-3 font-medium text-center">{infoLabel}</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-2 text-stone-50">
            {infoTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-red-400 mx-auto mb-12 rounded-full"></div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {infoItems.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-stone-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-stone-300 text-2xl">{item.icono}</span>
                </div>
                <h3 className="text-xl font-serif font-bold mb-3 text-stone-100">{item.titulo}</h3>
                <div className="w-8 h-px bg-stone-600 mx-auto mb-3"></div>
                <p className="text-stone-400 text-sm leading-relaxed">
                  {item.descripcion}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonacionesPage;